"use client";
import { useState, useRef } from "react";
import { Icon } from "@iconify/react";
import { InputField, TextareaField, SelectField } from "@/components/Fields";
import { PrismicRichText } from "@/components/PrismicRichText";
import ReCAPTCHA from "react-google-recaptcha";

const ContactForm = ({ slice, showText = false }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dateOfEvent: "",
    numberOfPeople: "",
    budget: "",
    venue: "",
    enquiryType: "",
    leadSource: "",
    message: "",
  });
  const [statusMessage, setStatusMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleDateChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      dateOfEvent: e.target.value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      dateOfEvent: "",
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = "First Name is required";
    if (!formData.lastName) newErrors.lastName = "Last Name is required";
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Email is not valid";
    }
    if (!formData.phoneNumber)
      newErrors.phoneNumber = "Phone Number is required";
    if (!formData.dateOfEvent)
      newErrors.dateOfEvent = "Date of Event is required";
    if (!formData.numberOfPeople)
      newErrors.numberOfPeople = "Number of People is required";
    if (!formData.budget) newErrors.budget = "Budget is required";
    if (!formData.enquiryType)
      newErrors.enquiryType = "Type of Enquiry is required";
    if (!formData.leadSource)
      newErrors.leadSource = "How did you hear about us? is required";
    if (!formData.message) newErrors.message = "Message is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage(""); // Clear previous status message

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setStatusMessage("Please fill in all required fields.");
      setIsSuccess(false);
      return;
    }

    try {
      const token = await recaptchaRef.current.executeAsync();
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...formData, captchaToken: token }),
      });

      if (response.ok) {
        setStatusMessage("Message sent successfully!");
        setIsSuccess(true);
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          dateOfEvent: "",
          numberOfPeople: "",
          budget: "",
          venue: "",
          enquiryType: "",
          leadSource: "",
          message: "",
        }); // Clear form fields
        recaptchaRef.current.reset(); // Reset reCAPTCHA
      } else {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          setStatusMessage(`Error: ${errorData.message}`);
        } else {
          const errorText = await response.text();
          setStatusMessage(`${errorText}`);
        }
        setIsSuccess(false);
      }
    } catch (error) {
      setStatusMessage("An unexpected error occurred. Please try again later.");
      setIsSuccess(false);
    }
  };

  const budgetOptions = [
    "£180 - £400",
    "£400 - £800",
    "£800 - £1500",
    "£1500 - £3000",
    "£3000+",
  ];
  const enquiryOptions = [
    "Wedding Cake",
    "Celebration Cake",
    "Corporate Event",
  ];
  const leadSourceOptions = ["Google Search", "Social Media", "Recommendation"];

  return (
    <div className="mx-auto relative py-20 bg-[#F0F9F7] rounded-md p-8">
      <div
        className={`flex gap-20 mx-auto items-start ${showText ? "max-w-7xl flex-col md:flex-row" : "flex-col"}`}
      >
        <form
          onSubmit={handleSubmit}
          noValidate
          className={`${showText ? "w-full md:w-2/3" : "w-full"} space-y-4  order-2 md:order-1`}
        >
          <h3 className="font-serif  tracking-wider mb-4 text-xl font-bold w-full order-2 md:order-1">
            Send us an enquiry
          </h3>
          <div className="text-sm text-gray-800 border-l-4 border-primary p-4 bg-[#F0F9F7] bg-white leading-6">
            When completing the form, please take time to cover all the fields,
            including the message section. Approach this section with as much
            detail and information as you can. This will help us build an idea
            of any special requirements you may have and enable us to better
            assist you on your journey to receiving the ideal cake. <br />
            <br />
            Please be advised that we prioritise contact forms that are
            completed and offer the most detail within the requirements. This
            allows us to deliver the most accurate quote for your prerequisites
            and ensures we process your order as quickly and efficiently as
            possible.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            <InputField
              label="First Name"
              name="firstName"
              placeholder="Enter your first name"
              value={formData.firstName}
              onChange={handleChange}
              required
              error={errors.firstName}
            />

            <InputField
              label="Last Name"
              name="lastName"
              placeholder="Enter your last name"
              value={formData.lastName}
              onChange={handleChange}
              required
              error={errors.lastName}
            />

            <InputField
              label="Email"
              name="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              required
              error={errors.email}
            />

            <InputField
              label="Phone Number"
              name="phoneNumber"
              type="phone"
              placeholder="Enter phone number"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              error={errors.phoneNumber}
            />

            <InputField
              label="Date of event"
              name="dateOfEvent"
              type="date"
              placeholder="Enter date of event"
              value={formData.dateOfEvent}
              onChange={handleDateChange}
              required
              error={errors.dateOfEvent}
            />
            <InputField
              label="Number of people"
              name="numberOfPeople"
              type="number"
              placeholder="Enter number of people"
              value={formData.numberOfPeople}
              onChange={handleChange}
              required
              error={errors.numberOfPeople}
            />
            <SelectField
              label="Budget"
              name="budget"
              value={formData.budget}
              options={budgetOptions}
              onChange={handleChange}
              required
              error={errors.budget}
            />
            <InputField
              label="Venue"
              name="venue"
              type="text"
              placeholder="Enter the venue (if known)"
              value={formData.venue}
              onChange={handleChange}
              required={false}
            />
            <SelectField
              label="Type of enquiry"
              name="enquiryType"
              value={formData.enquiryType}
              options={enquiryOptions}
              onChange={handleChange}
              required
              error={errors.enquiryType}
            />
            <SelectField
              label="How did you hear about us?"
              name="leadSource"
              value={formData.leadSource}
              options={leadSourceOptions}
              onChange={handleChange}
              required
              error={errors.leadSource}
            />
          </div>
          <div className="mt-2">
            <TextareaField
              label="Message"
              name="message"
              placeholder="Write your message here…"
              value={formData.message}
              onChange={handleChange}
              required
              error={errors.message}
            />
          </div>
          <div className="mt-4">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
              size="invisible"
            />
          </div>
          <button
            type="submit"
            className="bg-primary hover:bg-black text-white text-base font-medium h-full py-2 px-6 transition duration-300 ease-in-out focus:outline-none focus:ring-2 flex items-center"
          >
            Send message <Icon icon="mdi:envelope" className="w-4 h-6 ml-2" />
          </button>
          {statusMessage && (
            <div
              className={`mt-2 text-sm ${isSuccess ? "text-green-600" : "text-red-600"}`}
            >
              {statusMessage}
            </div>
          )}
        </form>

        {showText && (
          <div
            className={` ${showText ? "w-full md:w-1/3 mt-0 md:mt-10 order-1 md:order-2" : "w-full"}`}
          >
            <PrismicRichText field={slice.primary.description} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactForm;
